
import { BrowserRouter, Link, NavLink, Route, Routes } from "react-router-dom";
import { useRef, useState } from "react";
import { ClearPathLogo } from '@clearpath/design-system';
import { Alert, AlertDescription } from '@clearpath/design-system/ui/alert';
import { Button } from '@clearpath/design-system/ui/button';
import { Input } from '@clearpath/design-system/ui/input';
import { useAuth } from "./auth-provider";
import { FederationInboxPage } from "./pages/FederationInboxPage";
import { FederationOrderDetailPage } from "./pages/FederationOrderDetailPage";
import { OutboxPage } from "./pages/OutboxPage";
import { FederationLinksPage } from "./pages/FederationLinksPage";
import { OrderInbox } from "./pages/OrderInbox";
import { OrderShared } from "./pages/OrderShared";
import { IdleTimeoutGuard } from "./components/IdleTimeoutGuard";
import { AuthCallback } from "./pages/AuthCallback";

/**
 * Vendor app shell.
 *
 * The vendor is a separate ClearPath tenant with their own Cognito pool
 * (tenant-{id}-staff). This app is a thin "federated inbox" surface —
 * the same bundle would in theory mount inside apps/main under a
 * /federation route, but we ship it as its own app so that (a) the
 * default landing is the inbox, (b) the audience claim is narrowly
 * `clearpath-vendor`, and (c) we can deploy + scale it independently.
 */
export function App() {
  const { session, loading, mfaEnrollmentRequired, signIn, startMfaEnrollment, signOut } = useAuth();

  if (typeof window !== "undefined" && window.location.pathname === "/auth/callback") {
    return <AuthCallback />;
  }

  if (loading) {
    return <div className="loading">Loading…</div>;
  }

  // ENG-340 — a mandatory-MFA principal (vendor / tenant_staff_manager) with
  // no verified factor is held here and cannot reach any vendor surface until
  // they enroll. `session` is withheld by the provider while this is true, so
  // this branch must be checked BEFORE the `!session` sign-in branch below.
  if (mfaEnrollmentRequired) {
    return <MfaEnrollmentGate onEnroll={startMfaEnrollment} onSignOut={signOut} />;
  }

  // ENG-119 — same audience-boundary gap as apps/main. The fix lives in
  // packages/auth-client (the shared guard every SPA goes through): a
  // portal-customer's `.molaris.ai` refresh cookie (aud=clearpath-portal)
  // is never adopted as a session here (audience=clearpath-vendor), so the
  // pre-existing `!session` branch below is what redirects them to sign-in
  // — nothing vendor-console-shaped ever renders first.
  if (!session) {
    return (
      <div className="sign-in-shell">
        <ClearPathLogo variant="full" style={{ height: 28, width: 'auto', marginBottom: 8 }} />
        <h1 style={{ fontSize: '1rem', opacity: 0.8 }}>Vendor</h1>
        <p>Sign in to your vendor tenant to view incoming federated work.</p>
        <SignInForm onSubmit={signIn} />
      </div>
    );
  }

  return (
    <IdleTimeoutGuard>
    <BrowserRouter>
      <div className="authed-shell vendor-shell">
        <header>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClearPathLogo variant="full" style={{ height: 22, width: 'auto' }} />
              <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· Vendor</span>
            </Link>
          </h1>
          <nav aria-label="Primary">
            <NavLink to="/" end>
              Inbox
            </NavLink>
            <NavLink to="/unified">Unified</NavLink>
            <NavLink to="/outbox">Outbox</NavLink>
            <NavLink to="/federation-links">Connections</NavLink>
          </nav>
          <span className="session-email">{session.email}</span>
          <button type="button" onClick={() => void signOut()}>
            Sign out
          </button>
        </header>
        <main>
          <Routes>
            <Route path="/" element={<FederationInboxPage />} />
            <Route path="/inbox" element={<FederationInboxPage />} />
            <Route path="/unified" element={<OrderInbox />} />
            <Route path="/orders/:id" element={<FederationOrderDetailPage />} />
            <Route path="/shared/:id" element={<OrderShared />} />
            <Route path="/outbox" element={<OutboxPage />} />
            <Route path="/federation-links" element={<FederationLinksPage />} />
            <Route path="*" element={<FederationInboxPage />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
    </IdleTimeoutGuard>
  );
}

/**
 * ENG-3777 — collects ONLY the email, which is forwarded as the OIDC
 * login_hint. The password is entered on the hosted sign-in page (which also
 * carries "Forgot password?"), so this form never asks for a credential it
 * would throw away and password managers never save one against the vendor
 * origin. The email is optional: an empty submit still starts sign-in.
 */
function SignInForm({
  onSubmit,
}: {
  onSubmit: (email: string) => Promise<void>;
}) {
  // Stays true on success: the browser is navigating to the hosted page, and
  // a second submit would start a second PKCE redirect (same as main's Auth).
  const [redirecting, setRedirecting] = useState(false);
  // The ref closes the gap before the state update commits (two events in one tick).
  const inFlight = useRef(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (inFlight.current) return;
        inFlight.current = true;
        const fd = new FormData(e.currentTarget);
        setError(null);
        setRedirecting(true);
        try {
          await onSubmit(String(fd.get("email") ?? ""));
        } catch {
          inFlight.current = false;
          setRedirecting(false);
          setError(SIGN_IN_START_FAILED);
        }
      }}
    >
      <label>
        Email
        <Input type="email" name="email" autoComplete="email" onChange={() => setError(null)} />
      </label>
      <p className="sign-in-hint">
        You'll enter your password on the secure Molaris sign-in page, where you
        can also reset it.
      </p>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button type="submit" disabled={redirecting}>
        {redirecting ? "Redirecting…" : "Sign in"}
      </Button>
    </form>
  );
}

const SIGN_IN_START_FAILED = "We couldn't start sign-in. Check your connection and try again.";

/**
 * ENG-340 — blocking enrollment gate. Rendered instead of the vendor shell
 * when a signed-in mandatory-MFA principal holds no verified factor. No
 * protected surface is reachable from here: the only forward action is to
 * enroll (handed off to the hosted auth flow) or sign out.
 */
function MfaEnrollmentGate({
  onEnroll,
  onSignOut,
}: {
  onEnroll: () => Promise<void>;
  onSignOut: () => Promise<unknown>;
}) {
  return (
    <div className="sign-in-shell" role="alert" aria-live="assertive">
      <ClearPathLogo variant="full" style={{ height: 28, width: 'auto', marginBottom: 8 }} />
      <h1 style={{ fontSize: '1rem', opacity: 0.8 }}>Security setup required</h1>
      <p>
        Multi-factor authentication is required for vendor access. Set up an
        authenticator app or security key to continue — you won't be able to
        view federated work until a factor is enrolled.
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button variant="default" onClick={() => void onEnroll()}>
          Set up MFA
        </Button>
        <Button variant="secondary" onClick={() => void onSignOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );
}
